/* =========================================================================
   WMS S-25-05 · page structure config
   Источник структуры: wms-prototype.html (task-based flow). Порядок и
   ключи страниц следуют его NAV-дереву (Операции / Приёмка на склад /
   Склад / Выдача / Аналитика / Администрация).
   ========================================================================= */

export interface WmsPageConfig {
  /** URL segment under /wms */
  key: string
  /** Page title (Russian, как в прототипе) */
  title: string
  /** Sidebar section this page belongs to */
  section: string
  /**
   * Permission code required to view this page (from `/me` permissions).
   * Пусто/не задано ⇒ страница открыта любому аутентифицированному WMS-
   * пользователю (см. RequirePermission — falsy permission всегда пропускает).
   * Так помечены все страницы нового task-flow, у которых пока нет
   * реального бэкенда — им не нужно ждать серверных прав.
   */
  permission?: string
  /**
   * «Скоро» — страница ещё не готова к использованию (см. SCRUM-532):
   * маршрут недоступен вообще, независимо от прав — WmsRoutes.tsx уводит
   * на fallbackPath тем же способом, что и отказ RequirePermission, не
   * рендеря компонент страницы ни разу. Сайдбар (AsideMenuMain.tsx) держит
   * свой собственный, отдельный список comingSoon-пунктов — это два разных
   * места одного и того же названия не просто так: один блокирует переход,
   * другой только рисует состояние ссылки.
   */
  comingSoon?: boolean
}

export const WMS_SECTIONS = {
  operations: 'Операции',
  receiving: 'Приёмка на склад',
  warehouse: 'Склад',
  handover: 'Выдача',
  delivery: 'Доставка',
  analytics: 'Аналитика',
  directories: 'Справочники',
  admin: 'Администрация',
} as const

export const WMS_PAGES: WmsPageConfig[] = [
  // Операции
  {
    key: 'dashboard',
    title: 'Дашборд',
    section: WMS_SECTIONS.operations,
    permission: 'wms:dashboard:view',
  },
  {
    key: 'orders',
    title: 'Все заказы',
    section: WMS_SECTIONS.operations,
    permission: 'wms:page:all-orders:view',
  },
  {key: 'dispatch', title: 'Рабочее место диспетчера', section: WMS_SECTIONS.operations},
  {
    key: 'ttc',
    title: 'Поступления от ТТЦ',
    section: WMS_SECTIONS.operations,
    permission: 'wms:page:ttc-receipts:view',
  },

  // Приёмка на склад
  {
    key: 'tasks-search',
    title: 'Поиск',
    section: WMS_SECTIONS.receiving,
    permission: 'wms:page:tasks:view',
  },
  {
    key: 'tasks-movement',
    title: 'Перемещение',
    section: WMS_SECTIONS.receiving,
    permission: 'wms:page:tasks:view',
  },
  {
    key: 'tasks-acceptance',
    title: 'Приёмка и контроль',
    section: WMS_SECTIONS.receiving,
    permission: 'wms:page:tasks:view',
  },
  {
    key: 'tasks-placement',
    title: 'Размещение',
    section: WMS_SECTIONS.receiving,
    permission: 'wms:page:tasks:view',
  },

  // Склад
  {
    key: 'storage',
    title: 'Хранение',
    section: WMS_SECTIONS.warehouse,
    permission: 'wms:page:storage:view',
  },
  {
    key: 'moves',
    title: 'Внутренние перемещения',
    section: WMS_SECTIONS.warehouse,
    permission: 'wms:page:internal-moves:view',
  },
  {
    key: 'inventory',
    title: 'Инвентаризация',
    section: WMS_SECTIONS.warehouse,
    comingSoon: true,
  },

  // Выдача
  {
    key: 'requests',
    title: 'Заявки клиентов',
    section: WMS_SECTIONS.handover,
    permission: 'wms:page:client-requests:view',
  },
  {
    key: 'tasks-picking',
    title: 'Комплектация',
    section: WMS_SECTIONS.handover,
    permission: 'wms:page:tasks:view',
  },
  {
    key: 'tasks-control',
    title: 'Контроль комплектации',
    section: WMS_SECTIONS.handover,
    permission: 'wms:page:tasks:view',
  },
  {
    key: 'tasks-expedition',
    title: 'Экспедиция',
    section: WMS_SECTIONS.handover,
    permission: 'wms:page:tasks:view',
  },
  {
    key: 'tasks-handoff',
    title: 'Задания на выдачу',
    section: WMS_SECTIONS.handover,
    permission: 'wms:page:tasks:view',
  },
  {
    key: 'handoff',
    title: 'Выдача',
    section: WMS_SECTIONS.handover,
    permission: 'wms:page:handoff:view',
  },

  // Доставка (SCRUM-565). Ключ `delivery` — «К доставке»: на него уже ведут
  // ссылки «К доставке →» из «Заявок клиентов».
  {
    key: 'delivery',
    title: 'К доставке',
    section: WMS_SECTIONS.delivery,
    permission: 'wms:page:delivery:view',
  },
  {
    key: 'delivery-active',
    title: 'Активные доставки',
    section: WMS_SECTIONS.delivery,
    permission: 'wms:page:delivery:view',
  },
  {
    key: 'kassa',
    title: 'Касса',
    section: WMS_SECTIONS.delivery,
    permission: 'wms:page:kassa:view',
  },

  // Аналитика
  {key: 'reports', title: 'Отчёты', section: WMS_SECTIONS.analytics},
  {key: 'kpi', title: 'KPI сотрудников', section: WMS_SECTIONS.analytics},

  // Справочники (SCRUM-555; позже «Автомобили» переедут в группу «Доставка»)
  {
    key: 'vehicles',
    title: 'Автомобили',
    section: WMS_SECTIONS.directories,
    permission: 'wms:page:vehicles:view',
  },
  {
    key: 'geozones',
    title: 'Геозоны',
    section: WMS_SECTIONS.directories,
    permission: 'wms:page:geozones:view',
  },

  // Администрация
  {
    key: 'staff',
    title: 'Сотрудники склада',
    section: WMS_SECTIONS.admin,
    permission: 'wms:page:employees:view',
  },
  {
    key: 'pda-devices',
    title: 'Устройства ТСД',
    section: WMS_SECTIONS.admin,
    permission: 'wms:page:pda-devices:view',
  },
  {
    key: 'settings',
    title: 'Настройки сервиса',
    section: WMS_SECTIONS.admin,
    permission: 'wms:settings:manage',
  },
  {key: 'norms', title: 'Нормативы', section: WMS_SECTIONS.admin},
]

/** Quick lookup: page key → required permission code (empty string ⇒ open to any WMS user). */
export const WMS_PAGE_PERMISSION: Record<string, string> = WMS_PAGES.reduce((acc, p) => {
  acc[p.key] = p.permission ?? ''
  return acc
}, {} as Record<string, string>)

/** Права Кассира — у него только они (wms-route-handover.md, раздел 5). */
const KASSA_ONLY_CODES = ['wms:page:kassa:view', 'wms:delivery:cash-accept']

/**
 * Можно ли открыть страницу с таким кодом права. Страница без кода открыта
 * любому WMS-пользователю — кроме Кассира: ему не нужны заготовки
 * «Отчётов», «KPI» и т.п., только «Касса».
 */
export const canOpenPage = (permission: string | undefined, permissions: string[]): boolean => {
  if (permission) return permissions.includes(permission)
  const kassaOnly = permissions.length > 0 && permissions.every((p) => KASSA_ONLY_CODES.includes(p))
  return !kassaOnly
}
